import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import OrganisationIcon from 'components/Svgs/icons/Organisation';
import CoreDetailsIcon from '@mui/icons-material/Assignment';
import { styled, Theme } from '@mui/material';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';
import MenuItem from '@mui/material/MenuItem';
import { SectionTitle, TextV2 } from '@insights-ltd/design-library/components';
import {
  canChangeEventType,
  eventTypesAreCompatible,
  isDFCExperience,
  isIdtlExperience,
} from 'domain/event';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import OrganisationAutocomplete from 'components/OrganisationPicker/OrganisationAutocomplete';
import { OrganisationResponse } from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { EventType } from 'types/types';
import { useBetaEnabled } from 'features';
import { CoreDetailsData, EventForm } from './types';
import { Select } from './Select';
import useGetCoreDetailsData from './useGetCoreDetailsData';
import { getErrorMessage } from './validation';

const ARBITRARY_MAX_LENGTH = 100;

type CoreDetailsFormProps = {
  event: EventForm;
  isCreating?: boolean;
  onSubmit: (formData: CoreDetailsData) => void;
  children: ReactNode;
  invitedCount: number;
};

const StyledOrganisationIcon = styled(OrganisationIcon)(({ theme }) => ({
  fill: theme.palette.pink.dark,
  height: '20px',
}));

const StyledCoreDetailsIcon = styled(CoreDetailsIcon)(({ theme }) => ({
  fill: theme.palette.pink.dark,
  height: '20px',
}));

const StyledForm = styled('form')(({ theme }) => ({
  '> * + *': { marginTop: theme.spacing(spacingSizeMap.L) },
}));

const StyledDiv = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
    spacingSizeMap.XS,
  )} ${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(spacingSizeMap.S)}`,
  background: theme.palette.blue.light,
  borderRadius: '4px',
}));

const CoreDetailsForm = ({
  event,
  isCreating = false,
  onSubmit,
  children,
  invitedCount,
}: CoreDetailsFormProps) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<CoreDetailsData>({
    defaultValues: event,
  });
  const [eventType] = watch(['eventType']);

  const { t, i18n } = useTranslation();
  const criticalThinkingBetaEnabled = useBetaEnabled(
    'critical-thinking-workshop',
  );

  const {
    isPending,
    isError,
    setMyOrganisation,
    organisations,
    myOrganisation,
    showOrganisationDropdown,
    eventTypes,
  } = useGetCoreDetailsData(isCreating);

  useEffect(() => {
    if (setMyOrganisation) {
      register('organisation', { required: true });
    }
  }, [register, setMyOrganisation]);

  useEffect(() => {
    if (setMyOrganisation && myOrganisation) {
      setValue('organisation', myOrganisation);
    }
  }, [setValue, setMyOrganisation, myOrganisation]);

  if (isPending) {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (isError) {
    return (
      <FullScreenError
        message={t('ui.event-management.events.overview.error-loading-event')}
      />
    );
  }

  const eventTypeEditable =
    isCreating || (canChangeEventType(event.eventType) && invitedCount === 0);

  const filteredEventTypes = isCreating
    ? eventTypes
    : Object.fromEntries(
        Object.entries(eventTypes).filter(([otherEventType]) =>
          eventTypesAreCompatible(
            event.eventType as EventType,
            otherEventType as EventType,
          ),
        ),
      );

  const sortKey: EventType = 'INSIGHTS_DISCOVERY_WORKSHOP';

  const sortedFilteredEventTypes = Object.fromEntries(
    Object.entries(filteredEventTypes).sort(
      ([keyA, valueA], [keyB, valueB]) => {
        const priorityA = keyA === sortKey ? 0 : 1;
        const priorityB = keyB === sortKey ? 0 : 1;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        const translatedTextA = t(valueA.textKey);
        const translatedTextB = t(valueB.textKey);

        return translatedTextA.localeCompare(translatedTextB, i18n.language);
      },
    ),
  );

  if (
    isCreating &&
    eventType === 'INSIGHTS_DISCOVERY_ACCREDITATION' &&
    !eventTypes?.INSIGHTS_DISCOVERY_ACCREDITATION
  ) {
    setValue('eventType', '');
  }

  if (Object.keys(sortedFilteredEventTypes).length === 0) {
    return null;
  }

  return (
    <StyledForm noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      {!!organisations?.length && showOrganisationDropdown && (
        <>
          <SectionTitle
            icon={{ icon: <StyledOrganisationIcon />, iconColour: 'pink' }}
            text={t('ui.event-management.users.create.organisation', {
              count: 1,
            })}
          />
          <Controller
            name="organisation"
            defaultValue={
              setMyOrganisation && myOrganisation ? myOrganisation : undefined
            }
            rules={{ required: true }}
            control={control}
            render={({ field: { onChange: onReactHookFormChange, value } }) => {
              return (
                <OrganisationAutocomplete
                  variant="dropdown"
                  onSelect={(organisation: OrganisationResponse) => {
                    const org =
                      organisation.name === '' ? undefined : organisation;
                    if (org) onReactHookFormChange(organisation);
                  }}
                  currentOrg={value}
                  disableClearable
                  error={Boolean(errors.organisation)}
                  helperText={
                    errors?.organisation &&
                    t(getErrorMessage(errors.organisation))
                  }
                  required
                />
              );
            }}
          />
        </>
      )}
      <SectionTitle
        icon={{ icon: <StyledCoreDetailsIcon />, iconColour: 'pink' }}
        text={t('ui.event-management.events.create.core-details')}
      />
      <TextField
        id="name"
        label={t('ui.event-management.events.create.label.event-name')}
        required
        variant="outlined"
        fullWidth
        type="text"
        {...register('name', { required: true })}
        error={Boolean(errors.name)}
        helperText={errors?.name ? t(getErrorMessage(errors.name)) : null}
        sx={(theme) => ({
          marginTop: theme.spacing(spacingSizeMap.M),
        })}
        autoComplete="new-password"
        slotProps={{
          htmlInput: { maxLength: ARBITRARY_MAX_LENGTH },
        }}
      />
      <Select
        disabled={!eventTypeEditable}
        id="eventType"
        name="eventType"
        label={t('ui.event-management.events.create.label.event-type')}
        control={control}
        error={Boolean(errors.eventType)}
        rules={{ required: true }}
        errorMessage={
          errors?.eventType ? t(getErrorMessage(errors.eventType)) : ''
        }
        sx={(theme: Theme) => ({
          marginTop: theme.spacing(spacingSizeMap.L),
        })}
      >
        {Object.entries(sortedFilteredEventTypes)
          .filter(
            ([value]) =>
              criticalThinkingBetaEnabled ||
              value !== 'INSIGHTS_CRITICAL_THINKING',
          )
          .map(([value, { textKey }]) => (
            <MenuItem key={value} value={value}>
              {t(textKey)}
            </MenuItem>
          ))}
      </Select>
      {isIdtlExperience(eventType) && (
        <StyledDiv>
          <TextV2 component="p" variant="bodyBold">
            {t(
              'ui.event-management.events.create.event-type.insights-discovery-transformational-leadership-profile-message',
            )}
          </TextV2>
        </StyledDiv>
      )}
      {isDFCExperience(eventType) && (
        <StyledDiv>
          <TextV2 variant="bodyBold">
            {t(
              'ui.event-management.events.create.event-type.insights-discovery-full-circle-profile-message',
            )}
          </TextV2>
        </StyledDiv>
      )}
      {children}
    </StyledForm>
  );
};

export default CoreDetailsForm;
